/* begin general import */
import { Col, Row } from "antd";
import { ASSETS_SVG } from "config/consts";
import { AppUserGrouping } from "models/AppUserGrouping";
import { Status, StatusFilter } from "models/Status";
import React from "react";
import { useTranslation } from "react-i18next";
import { Model } from "react3l-common";
import { Modal, Select } from "react3l-ui-library";

import { DrawerProps } from "react3l-ui-library/build/components/Drawer/Drawer";
import FormItem from "react3l-ui-library/build/components/FormItem";
import InputText from "react3l-ui-library/build/components/Input/InputText";
import { appUserGroupingRepository } from "repositories/app-user-grouping-repository";
import nameof from "ts-nameof.macro";
import { utilService } from "services/common-services/util-service";
import { filterService } from "services/page-services/filter-service";
import { Organization, OrganizationFilter } from "models/Organization";
import { authorizationService } from "services/common-services/authorization-service";
import { APP_USER_GROUPING_ROUTE } from "config/route-consts";
import { API_APP_USER_GROUPING_PREFIX } from "config/api-consts";
import { Close20 } from "@carbon/icons-react";
import _ from "lodash";
import TreeSelect from "components/TreeSelect";

interface AppUserGroupingDetailDrawerProps extends DrawerProps {
  model: AppUserGrouping;
  handleChangeSingleField?: (config: {
    fieldName: string;
  }) => (value: any) => void;
  handleChangeTreeField?: (config: {
    fieldName: string;
  }) => (values: any[], isMultiple: boolean) => void;
  handleChangeAllField?: (data: any) => void;
  loading?: boolean;
  currentNode?: AppUserGrouping;
  handleChangeSelectField: (config: {
    fieldName: string;
  }) => (idValue: number, value: Model) => void;
  currentOrganization?: Organization;
}

function AppUserGroupingDetailDrawer(props: AppUserGroupingDetailDrawerProps) {
  const [translate] = useTranslation();

  const { validAction } = authorizationService.useAuthorizedAction(
    APP_USER_GROUPING_ROUTE.split("/")[
      APP_USER_GROUPING_ROUTE.split("/").length - 1
    ],
    API_APP_USER_GROUPING_PREFIX
  );

  const {
    model,
    handleChangeTreeField,
    handleChangeSingleField,
    handleChangeAllField,
    handleChangeSelectField,
    loading,
    visible,
    handleSave,
    handleCancel,
    currentOrganization,
  } = props;

  const [statusList] = filterService.useEnumList<Status>(
    appUserGroupingRepository.singleListStatus
  );

  React.useEffect(() => {
    if (model?.statusId === undefined && statusList?.length > 0) {
      const newModel: AppUserGrouping = { ...model };
      newModel.statusId = statusList[1]?.id;
      newModel.status = statusList[1];
      if (currentOrganization?.id) {
        newModel.organization = currentOrganization;
        newModel.organizationId = currentOrganization?.id;
      }
      handleChangeAllField({ ...newModel });
    }
  }, [
    currentOrganization,
    handleChangeAllField,
    handleChangeSelectField,
    model,
    statusList,
  ]);

  const handleChangeStatus = React.useCallback(
    (statusId: number, status: Status) => {
      if (statusId !== null) {
        handleChangeSelectField({ fieldName: "status" })(statusId, status);
      } else {
        handleChangeSelectField({ fieldName: "status" })(1, undefined);
      }
    },

    [handleChangeSelectField]
  );
  return (
    <Modal
      size={720}
      visibleFooter={true}
      visible={visible}
      title={
        model?.id
          ? translate("general.detail.title")
          : translate("general.actions.create")
      }
      handleSave={
        (!model?.id && validAction("create")) ||
        (model?.id && validAction("update"))
          ? handleSave
          : undefined
      }
      handleCancel={handleCancel}
      titleButtonCancel={translate("general.actions.close")}
      titleButtonApply={translate("general.actions.save")}
      closable={false}
      wrapClassName={"dxg-modal-wrap"}
    >
      <div className="modal-content">
        {loading ? (
          <div className="loading-block">
            <img src={ASSETS_SVG + "/spinner.svg"} alt="Loading..." />
          </div>
        ) : (
          <Row className="d-flex">
            <Col lg={24}>
              <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
                <Col lg={12} className="m-b--sm m-t--sm">
                  <FormItem
                    validateStatus={utilService.getValidateStatus(
                      model,
                      nameof(model.code)
                    )}
                    message={model.errors?.code}
                  >
                    <InputText
                      label={translate("appUserGroupings.code")}
                      type={0}
                      isRequired
                      value={model.code}
                      placeHolder={translate(
                        "appUserGroupings.placeholder.code"
                      )}
                      className={"tio-account_square_outlined"}
                      onChange={handleChangeSingleField({
                        fieldName: nameof(model.code),
                      })}
                    />
                  </FormItem>
                </Col>

                <Col lg={12} className="m-b--sm m-t--sm">
                  <FormItem
                    validateStatus={utilService.getValidateStatus(
                      model,
                      nameof(model.name)
                    )}
                    message={model.errors?.name}
                  >
                    <InputText
                      isRequired
                      label={translate("appUserGroupings.name")}
                      type={0}
                      value={model.name}
                      placeHolder={translate(
                        "appUserGroupings.placeholder.name"
                      )}
                      className={"tio-account_square_outlined"}
                      onChange={handleChangeSingleField({
                        fieldName: nameof(model.name),
                      })}
                    />
                  </FormItem>
                </Col>

                <Col lg={12} className="m-b--sm m-t--sm">
                  <FormItem
                    validateStatus={utilService.getValidateStatus(
                      model,
                      nameof(model.status)
                    )}
                    message={model.errors?.status}
                  >
                    <Select
                      label={translate("appUserGroupings.status")}
                      type={0}
                      classFilter={StatusFilter}
                      searchProperty={"name"}
                      placeHolder={translate(
                        "appUserGroupings.placeholder.status"
                      )}
                      getList={appUserGroupingRepository.singleListStatus}
                      onChange={handleChangeStatus}
                      value={model.status}
                    />
                  </FormItem>
                </Col>

                <Col lg={12} className="m-b--sm m-t--sm">
                  <FormItem
                    validateStatus={utilService.getValidateStatus(
                      model,
                      nameof(model.organization)
                    )}
                    message={model.errors?.organization}
                  >
                    <TreeSelect
                      isRequired
                      label={translate("appUserGroupings.organization")}
                      type={0}
                      placeHolder={translate(
                        "appUserGroupings.placeholder.organization"
                      )}
                      selectable={true}
                      classFilter={OrganizationFilter}
                      onChange={(values: any[]) => {
                        const internalValues =
                          values && values?.length > 0
                            ? values.map((value: Organization) => {
                                value.organizationId = value?.id;
                                value.appUserGroupingId = 0;
                                return value;
                              })
                            : [];
                        handleChangeTreeField({
                          fieldName: nameof(
                            model.appUserGroupingOrganizationMappings
                          ),
                        })(internalValues, true);
                      }}
                      checkStrictly
                      getTreeData={
                        appUserGroupingRepository.singleListOrganization
                      }
                      checkable
                      isUsingSearch
                      searchProperty="search"
                      searchType=""
                      disabled={
                        model?.parentId ||
                        !_.isNil(model?.id) ||
                        model?.id === 0
                          ? true
                          : false
                      }
                      listItem={model.appUserGroupingOrganizationMappings}
                      render={(object: Organization) => {
                        return object?.code && object?.name
                          ? `${object?.code} - ${object?.name}`
                          : null;
                      }}
                    />
                  </FormItem>
                </Col>
              </Row>
            </Col>
          </Row>
        )}
      </div>

      <span className={"close-modal"} onClick={handleCancel}>
        <Close20 />
      </span>
    </Modal>
  );
}

export default AppUserGroupingDetailDrawer;
